import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import {
  Search,
  Globe,
  Building,
  Users,
  Mail,
  MapPin,
  Plus,
  Trash2,
  CheckCircle,
  Loader2,
  Upload,
  Building2,
  Phone,
  User,
  ShieldCheck,
  UserSearch,
  RefreshCw,
  Clock,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { API } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const INDUSTRIES = [
  "Financial Services",
  "Technology",
  "Healthcare",
  "Manufacturing",
  "Retail",
  "Energy",
  "Telecommunications",
  "Professional Services",
  "Banking",
  "Insurance",
  "Real Estate",
];

const COUNTRIES = [
  "Nigeria",
  "Kenya",
  "South Africa",
  "Ghana",
  "Egypt",
  "Morocco",
  "Tanzania",
  "Uganda",
  "Rwanda",
  "Ethiopia",
  "United Kingdom",
  "United States",
  "United Arab Emirates",
];

export default function Research({ user }) {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [industry, setIndustry] = useState("Financial Services");
  const [location, setLocation] = useState("Nigeria");
  const [limit, setLimit] = useState("10");
  
  // Single company search
  const [companyName, setCompanyName] = useState("");
  const [companyCountry, setCompanyCountry] = useState("Nigeria");
  const [companyDomain, setCompanyDomain] = useState("");
  const [searchingCompany, setSearchingCompany] = useState(false);
  const [searchPreview, setSearchPreview] = useState(null); // G3b preview; null = nothing pending
  const [acceptingPreview, setAcceptingPreview] = useState(false);
  const [candidateSet, setCandidateSet] = useState(null); // Block1 discovery/bulk preview set; null = none pending
  const [candidateChecked, setCandidateChecked] = useState({}); // candidate_id -> bool
  const [acceptingSet, setAcceptingSet] = useState(false);
  
  // Bulk upload
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Email Finder
  const [finderDomain, setFinderDomain] = useState("");
  const [finderFirstName, setFinderFirstName] = useState("");
  const [finderLastName, setFinderLastName] = useState("");
  const [findingEmail, setFindingEmail] = useState(false);
  const [foundEmail, setFoundEmail] = useState(null);
  
  // Email Verifier
  const [verifyEmail, setVerifyEmail] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  
  // Person Enrichment
  const [enrichEmail, setEnrichEmail] = useState("");
  const [enriching, setEnriching] = useState(false);
  const [enrichResult, setEnrichResult] = useState(null);

  // Existing clients for enrichment
  const [existingClients, setExistingClients] = useState([]);
  const [showEnrichDialog, setShowEnrichDialog] = useState(false);
  const [selectedLeadForEnrich, setSelectedLeadForEnrich] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [enrichingClient, setEnrichingClient] = useState(false);
  const [activeTab, setActiveTab] = useState("company");

  // G3a — truthful counts, selection, and the decision modals.
  const [statusCounts, setStatusCounts] = useState({});
  const [collectionTotal, setCollectionTotal] = useState(0);
  const [leadView, setLeadView] = useState("working"); // working | all | new | no_results | converted | rejected | parked
  const [selectedLeadIds, setSelectedLeadIds] = useState(() => new Set());
  const [decisionModal, setDecisionModal] = useState(null); // { kind:'reject'|'park', bulk:bool, leadId?, count? }
  const [decisionReason, setDecisionReason] = useState("");
  const [decisionDate, setDecisionDate] = useState("");
  const [savingDecision, setSavingDecision] = useState(false);

  useEffect(() => {
    fetchLeads();
    fetchExistingClients();
  }, [leadView]);

  const fetchExistingClients = async () => {
    try {
      const response = await fetch(`${API}/clients`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setExistingClients(data);
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const fetchLeads = async () => {
    try {
      const statusViews = ["new", "no_results", "converted", "rejected", "parked"];
      const qs = statusViews.includes(leadView) ? `status=${leadView}` : `view=${leadView}`;
      const response = await fetch(`${API}/research/leads?${qs}&limit=2000`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setLeads(data.leads || []);
        setStatusCounts(data.status_counts || {});
        setCollectionTotal(data.collection_total || 0);
        setSelectedLeadIds(new Set());
      }
    } catch (error) {
      console.error("Error fetching leads:", error);
    } finally {
      setLoading(false);
    }
  };

  // Industry-based discovery search
  const handleDiscoverySearch = async () => {
    setSearching(true);
    try {
      const response = await fetch(`${API}/research/find-companies`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          industry,
          location,
          limit: parseInt(limit),
        }),
      });

      if (response.ok) {
        const result = await response.json();
        const cands = result.candidates || [];
        const acceptable = cands.filter((c) => !c.search_failed && !c.existing_match);
        setCandidateSet({ source: "discovery", candidates: cands });
        setCandidateChecked(Object.fromEntries(acceptable.map((c) => [c.candidate_id, true])));
        toast.success(`Found ${cands.length} — review and accept the ones worth keeping. Nothing is saved yet.`);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Search failed");
      }
    } catch (error) {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  // Single company search
  const handleCompanySearch = async () => {
    if (!companyName.trim() && !companyDomain.trim()) {
      toast.error("Please enter a company name, domain, or both");
      return;
    }
    
    setSearchingCompany(true);
    try {
      const response = await fetch(`${API}/research/company`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          company_name: companyName || null,
          country: companyCountry,
          domain: companyDomain || null,
          commit: false,  // G3b: preview only — nothing is created until accepted
        }),
      });

      if (response.ok) {
        const result = await response.json();
        setSearchPreview(result);  // open the accept/decline card; creates nothing yet
      } else if (response.status === 429) {
        toast.error("Hunter.io API rate limit reached. Please check your Hunter.io account or try again later.", { duration: 8000 });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Search failed");
      }
    } catch (error) {
      toast.error("Search failed");
    } finally {
      setSearchingCompany(false);
    }
  };

  // G3b — a person accepts a previewed result; this is the only thing that creates a lead.
  const acceptPreview = async () => {
    if (!searchPreview?.found) return;
    setAcceptingPreview(true);
    try {
      const f = searchPreview.found;
      const response = await fetch(`${API}/research/leads/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...f, searched_term: searchPreview.searched }),
      });
      const b = await response.json().catch(() => ({}));
      if (!response.ok) { toast.error(b.detail || "Could not accept"); return; }
      toast.success(`Lead accepted: ${f.company_name}`);
      setSearchPreview(null);
      setCompanyName("");
      setCompanyDomain("");
      fetchLeads();
    } catch { toast.error("Network error"); }
    finally { setAcceptingPreview(false); }
  };

  const declinePreview = () => { setSearchPreview(null); toast.info("Declined — nothing was created"); };

  // Block1 — accept the checked candidates from a discovery/bulk set. Same single
  // writer (/research/leads/accept). Declining = closing; nothing is created.
  const acceptCandidateSet = async () => {
    if (!candidateSet) return;
    const chosen = candidateSet.candidates.filter(
      (c) => candidateChecked[c.candidate_id] && !c.search_failed && c.contacts_found > 0
    );
    if (chosen.length === 0) { toast.error("Select at least one result with contacts"); return; }
    setAcceptingSet(true);
    try {
      const leadsPayload = chosen.map((c) => ({ ...c.found, searched_term: c.searched }));
      const response = await fetch(`${API}/research/leads/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ leads: leadsPayload }),
      });
      const b = await response.json().catch(() => ({}));
      if (!response.ok) { toast.error(b.detail || "Could not accept"); return; }
      toast.success(`Accepted ${b.accepted} lead(s)${b.refused ? `, ${b.refused} refused` : ""}`);
      setCandidateSet(null);
      setCandidateChecked({});
      fetchLeads();
    } catch { toast.error("Network error"); }
    finally { setAcceptingSet(false); }
  };

  const declineCandidateSet = () => {
    setCandidateSet(null);
    setCandidateChecked({});
    toast.info("Declined — nothing was created");
  };

  const toggleCandidate = (id) =>
    setCandidateChecked((prev) => ({ ...prev, [id]: !prev[id] }));


  const openExistingMatch = () => {
    const m = searchPreview?.existing_match;
    if (m?.type === "client" && m.id) navigate(`/clients/${m.id}`);
    else if (m?.type === "lead") { setSearchPreview(null); setLeadView("all"); fetchLeads(); toast.info("This company is already on the lead board"); }
  };


  // Bulk upload
  const handleBulkUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await fetch(`${API}/research/bulk-upload`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        const cands = result.candidates || [];
        const acceptable = cands.filter((c) => !c.search_failed && !c.existing_match);
        setCandidateSet({ source: "bulk_upload", candidates: cands, errors: result.errors || [] });
        setCandidateChecked(Object.fromEntries(acceptable.map((c) => [c.candidate_id, true])));
        toast.success(`Processed ${cands.length} rows — review and accept. Nothing is saved yet.`);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Upload failed");
      }
    } catch (error) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleConvertToClient = async (leadId) => {
    try {
      const response = await fetch(`${API}/research/leads/${leadId}/convert`, {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        const result = await response.json();
        if (result.existing) {
          toast.success(result.message || "Client already exists — contacts enriched", { duration: 5000 });
        } else {
          toast.success("New client created from lead");
        }
        fetchLeads();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to convert lead");
      }
    } catch (error) {
      toast.error("Failed to convert lead");
    }
  };

  // G3a — open the decision modal (single or bulk) for "not worth it" / "park".
  const openDecision = (kind, { leadId = null, bulk = false } = {}) => {
    setDecisionModal({ kind, leadId, bulk, count: bulk ? selectedLeadIds.size : 1 });
    setDecisionReason("");
    setDecisionDate("");
  };

  const toggleSelect = (leadId) => {
    setSelectedLeadIds((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId); else next.add(leadId);
      return next;
    });
  };

  const submitDecision = async () => {
    if (!decisionModal) return;
    const { kind, bulk, leadId } = decisionModal;
    if (kind === "reject" && !decisionReason.trim()) { toast.error("A reason is required."); return; }
    if (kind === "park" && !decisionDate) { toast.error("A review date is required."); return; }
    setSavingDecision(true);
    try {
      let response;
      if (bulk) {
        response = await fetch(`${API}/research/leads/bulk-decide`, {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ lead_ids: Array.from(selectedLeadIds), decision: kind,
            reason: decisionReason.trim(), review_date: decisionDate }),
        });
      } else {
        response = await fetch(`${API}/research/leads/${leadId}/${kind}`, {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ reason: decisionReason.trim(), review_date: decisionDate }),
        });
      }
      const b = await response.json().catch(() => ({}));
      if (!response.ok) { toast.error(b.detail || "Could not record the decision"); return; }
      toast.success(bulk
        ? `${kind === "park" ? "Parked" : "Marked not worth it"}: ${b.updated} lead${b.updated === 1 ? "" : "s"}`
        : (kind === "park" ? `Parked until ${decisionDate}` : "Marked not worth it"));
      setDecisionModal(null);
      fetchLeads();
    } catch {
      toast.error("Network error");
    } finally {
      setSavingDecision(false);
    }
  };

  const handleOpenEnrichDialog = (lead) => {
    setSelectedLeadForEnrich(lead);
    setSelectedClientId("");
    setShowEnrichDialog(true);
  };

  const handleEnrichExistingClient = async () => {
    if (!selectedClientId || !selectedLeadForEnrich) {
      toast.error("Please select a client to enrich");
      return;
    }

    setEnrichingClient(true);
    try {
      const response = await fetch(
        `${API}/research/leads/${selectedLeadForEnrich.lead_id}/enrich/${selectedClientId}`,
        {
          method: "POST",
          credentials: "include",
        }
      );

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || `Added ${result.contacts_added} contacts`);
        setLeads((prev) =>
          prev.map((l) =>
            l.lead_id === selectedLeadForEnrich.lead_id
              ? { ...l, status: "converted" }
              : l
          )
        );
        setShowEnrichDialog(false);
        setSelectedLeadForEnrich(null);
        setSelectedClientId("");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to enrich client");
      }
    } catch (error) {
      toast.error("Failed to enrich client");
    } finally {
      setEnrichingClient(false);
    }
  };

  // Email Finder handler
  const handleFindEmail = async () => {
    if (!finderDomain.trim() || !finderFirstName.trim() || !finderLastName.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setFindingEmail(true);
    setFoundEmail(null);
    
    try {
      const response = await fetch(`${API}/research/find-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          domain: finderDomain,
          first_name: finderFirstName,
          last_name: finderLastName,
        }),
      });

      const result = await response.json();
      
      if (result.status === "success" && result.email) {
        setFoundEmail(result);
        toast.success(`Found email: ${result.email}`);
      } else {
        toast.error(result.message || "Email not found");
        setFoundEmail({ status: "not_found", ...result });
      }
    } catch (error) {
      toast.error("Email search failed");
    } finally {
      setFindingEmail(false);
    }
  };

  // Email Verifier handler
  const handleVerifyEmail = async () => {
    if (!verifyEmail.trim() || !verifyEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    setVerifying(true);
    setVerifyResult(null);
    
    try {
      const response = await fetch(`${API}/research/verify-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: verifyEmail }),
      });

      if (response.ok) {
        const result = await response.json();
        setVerifyResult(result);
        const resultText = result.result === "deliverable" ? "Valid" : result.result;
        toast.success(`Email verification: ${resultText} (Score: ${result.score}%)`);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Verification failed");
      }
    } catch (error) {
      toast.error("Email verification failed");
    } finally {
      setVerifying(false);
    }
  };

  // Person Enrichment handler
  const handleEnrichPerson = async () => {
    if (!enrichEmail.trim() || !enrichEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    setEnriching(true);
    setEnrichResult(null);
    
    try {
      const response = await fetch(`${API}/research/enrich-person`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: enrichEmail }),
      });

      const result = await response.json();
      
      if (result.status === "success") {
        setEnrichResult(result);
        toast.success(`Found: ${result.full_name || result.email}`);
      } else {
        toast.error(result.message || "Person not found");
        setEnrichResult({ status: "not_found", email: enrichEmail });
      }
    } catch (error) {
      toast.error("Person enrichment failed");
    } finally {
      setEnriching(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="research-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">
          AI Research Assistant
        </h1>
        <p className="text-slate-500 mt-1">
          Find contact details for companies using Hunter.io
        </p>
      </div>

      {/* Search Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-6 mb-4">
          <TabsTrigger value="company" data-testid="tab-company-search">
            <Building2 className="w-4 h-4 mr-2" />
            Company
          </TabsTrigger>
          <TabsTrigger value="email-finder" data-testid="tab-email-finder">
            <UserSearch className="w-4 h-4 mr-2" />
            Find Email
          </TabsTrigger>
          <TabsTrigger value="verify" data-testid="tab-verify">
            <ShieldCheck className="w-4 h-4 mr-2" />
            Verify
          </TabsTrigger>
          <TabsTrigger value="enrich" data-testid="tab-enrich">
            <User className="w-4 h-4 mr-2" />
            Enrich
          </TabsTrigger>
          <TabsTrigger value="bulk" data-testid="tab-bulk-upload">
            <Upload className="w-4 h-4 mr-2" />
            Bulk
          </TabsTrigger>
          <TabsTrigger value="discover" data-testid="tab-discover">
            <Globe className="w-4 h-4 mr-2" />
            Discover
          </TabsTrigger>
        </TabsList>

        {/* Single Company Search */}
        <TabsContent value="company">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Research a Specific Company
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label>Company Name</Label>
                  <Input
                    placeholder="e.g., Dangote Industries"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="mt-1"
                    data-testid="company-name-input"
                    onKeyDown={(e) => e.key === "Enter" && handleCompanySearch()}
                  />
                </div>
                <div>
                  <Label>Domain</Label>
                  <Input
                    placeholder="e.g., dangote.com"
                    value={companyDomain}
                    onChange={(e) => setCompanyDomain(e.target.value)}
                    className="mt-1"
                    data-testid="company-domain-input"
                    onKeyDown={(e) => e.key === "Enter" && handleCompanySearch()}
                  />
                </div>
                <div>
                  <Label>Country</Label>
                  <Select value={companyCountry} onValueChange={setCompanyCountry}>
                    <SelectTrigger className="mt-1" data-testid="company-country-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((country) => (
                        <SelectItem key={country} value={country}>
                          {country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={handleCompanySearch}
                    disabled={searchingCompany || (!companyName.trim() && !companyDomain.trim())}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid="search-company-btn"
                  >
                    {searchingCompany ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Searching...
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        Find Contacts
                      </>
                    )}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-3">
                Search by company name, domain (e.g., dangote.com), or both for best results.
              </p>

              {/* G3b — preview card: nothing is created until accepted */}
              {searchPreview && (
                <div className="mt-4 border-2 border-indigo-200 rounded-lg p-4 bg-indigo-50/40" data-testid="search-preview">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">Review before accepting — nothing is saved yet</p>
                    <Badge variant="outline" className="text-[10px]">Searched: {searchPreview.searched}</Badge>
                  </div>

                  {searchPreview.name_mismatch && (
                    <div className="mb-2 text-xs text-amber-800 bg-amber-100 border border-amber-300 rounded px-2 py-1.5" data-testid="preview-mismatch">
                      ⚑ The result doesn't resemble what you searched. You searched <b>“{searchPreview.searched}”</b> but this was found as <b>“{searchPreview.found?.company_name}”</b> ({searchPreview.found?.domain}). Check before accepting.
                    </div>
                  )}

                  {searchPreview.existing_match && (
                    <div className="mb-2 text-xs text-blue-900 bg-blue-100 border border-blue-300 rounded px-2 py-1.5" data-testid="preview-existing-match">
                      Already on record as {searchPreview.existing_match.type === "client" ? "a client" : "a lead"}: <b>{searchPreview.existing_match.name}</b>
                      {searchPreview.existing_match.matched_on ? ` (matched on “${searchPreview.existing_match.matched_on}”)` : ""}.{" "}
                      <button className="underline font-medium" onClick={openExistingMatch} data-testid="preview-open-existing">Open it instead</button>.
                    </div>
                  )}

                  <div className="text-sm text-slate-800">
                    <p className="font-semibold">{searchPreview.found?.company_name} <span className="font-normal text-slate-500">· {searchPreview.found?.domain || "no domain"}</span></p>
                    <p className="text-xs text-slate-500 mt-0.5">{searchPreview.contacts_found} contact{searchPreview.contacts_found === 1 ? "" : "s"} found</p>
                  </div>

                  {searchPreview.search_failed ? (
                    <div className="mt-3 text-sm text-amber-800" data-testid="preview-search-failed">
                      This search found no contacts — a failed lookup, not a prospect. Nothing will be created.
                      <div className="mt-2"><Button size="sm" variant="outline" onClick={declinePreview} data-testid="preview-dismiss-btn">Dismiss</Button></div>
                    </div>
                  ) : (
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" disabled={acceptingPreview} onClick={acceptPreview} data-testid="preview-accept-btn">
                        {acceptingPreview ? <Loader2 className="w-4 h-4 animate-spin" /> : "Accept — add to board"}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={declinePreview} data-testid="preview-decline-btn">Decline</Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Finder Tab */}
        <TabsContent value="email-finder">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <UserSearch className="w-5 h-5" />
                Find Email by Name
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label>First Name *</Label>
                  <Input
                    value={finderFirstName}
                    onChange={(e) => setFinderFirstName(e.target.value)}
                    placeholder="e.g., Patrick"
                    data-testid="finder-first-name"
                  />
                </div>
                <div>
                  <Label>Last Name *</Label>
                  <Input
                    value={finderLastName}
                    onChange={(e) => setFinderLastName(e.target.value)}
                    placeholder="e.g., Collison"
                    data-testid="finder-last-name"
                  />
                </div>
                <div>
                  <Label>Domain *</Label>
                  <Input
                    value={finderDomain}
                    onChange={(e) => setFinderDomain(e.target.value)}
                    placeholder="e.g., stripe.com"
                    data-testid="finder-domain"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={handleFindEmail}
                    disabled={findingEmail || !finderFirstName.trim() || !finderLastName.trim() || !finderDomain.trim()}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid="find-email-btn"
                  >
                    {findingEmail ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Finding...
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        Find Email
                      </>
                    )}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-3">
                Enter a person&apos;s name and company domain to find their email address.
              </p>
              
              {/* Email Finder Result */}
              {foundEmail && (
                <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                  {foundEmail.status === "success" && foundEmail.email ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="font-semibold text-green-700">Email Found!</span>
                        <Badge variant="outline" className="ml-2">
                          Score: {foundEmail.score}%
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mt-3">
                        <div>
                          <span className="text-sm text-slate-500">Email:</span>
                          <p className="font-medium">{foundEmail.email}</p>
                        </div>
                        {foundEmail.position && (
                          <div>
                            <span className="text-sm text-slate-500">Position:</span>
                            <p className="font-medium">{foundEmail.position}</p>
                          </div>
                        )}
                        {foundEmail.company && (
                          <div>
                            <span className="text-sm text-slate-500">Company:</span>
                            <p className="font-medium">{foundEmail.company}</p>
                          </div>
                        )}
                        {foundEmail.linkedin && (
                          <div>
                            <span className="text-sm text-slate-500">LinkedIn:</span>
                            <a href={foundEmail.linkedin} target="_blank" rel="noopener noreferrer" 
                               className="text-blue-600 hover:underline text-sm">View Profile</a>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-amber-600">
                      <Mail className="w-5 h-5" />
                      <span>Email not found for {foundEmail.first_name} {foundEmail.last_name} @ {foundEmail.domain}</span>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Verifier Tab */}
        <TabsContent value="verify">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ShieldCheck className="w-5 h-5" />
                Verify Email Address
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <Label>Email Address *</Label>
                  <Input
                    type="email"
                    value={verifyEmail}
                    onChange={(e) => setVerifyEmail(e.target.value)}
                    placeholder="e.g., patrick@stripe.com"
                    data-testid="verify-email-input"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={handleVerifyEmail}
                    disabled={verifying || !verifyEmail.trim()}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid="verify-email-btn"
                  >
                    {verifying ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        <ShieldCheck className="w-4 h-4 mr-2" />
                        Verify Email
                      </>
                    )}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-3">
                Check if an email address is valid and deliverable.
              </p>
              
              {/* Verify Result */}
              {verifyResult && (
                <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      {verifyResult.result === "deliverable" ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : verifyResult.result === "risky" ? (
                        <ShieldCheck className="w-5 h-5 text-amber-600" />
                      ) : (
                        <Mail className="w-5 h-5 text-red-600" />
                      )}
                      <span className={`font-semibold ${
                        verifyResult.result === "deliverable" ? "text-green-700" :
                        verifyResult.result === "risky" ? "text-amber-700" : "text-red-700"
                      }`}>
                        {verifyResult.result === "deliverable" ? "Valid Email" :
                         verifyResult.result === "risky" ? "Risky Email" :
                         verifyResult.result === "undeliverable" ? "Invalid Email" : "Unknown"}
                      </span>
                      <Badge variant="outline" className="ml-2">
                        Score: {verifyResult.score}%
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <span className="text-slate-500">MX Records:</span>
                        <p className={verifyResult.mx_records ? "text-green-600" : "text-red-600"}>
                          {verifyResult.mx_records ? "✓ Found" : "✗ Missing"}
                        </p>
                      </div>
                      <div>
                        <span className="text-slate-500">SMTP Check:</span>
                        <p className={verifyResult.smtp_check ? "text-green-600" : "text-red-600"}>
                          {verifyResult.smtp_check ? "✓ Valid" : "✗ Failed"}
                        </p>
                      </div>
                      <div>
                        <span className="text-slate-500">Disposable:</span>
                        <p className={!verifyResult.disposable ? "text-green-600" : "text-red-600"}>
                          {verifyResult.disposable ? "✗ Yes" : "✓ No"}
                        </p>
                      </div>
                      <div>
                        <span className="text-slate-500">Accept All:</span>
                        <p className="text-slate-600">
                          {verifyResult.accept_all ? "Yes" : "No"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Person Enrichment Tab */}
        <TabsContent value="enrich">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="w-5 h-5" />
                Person Enrichment
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <Label>Email Address *</Label>
                  <Input
                    type="email"
                    value={enrichEmail}
                    onChange={(e) => setEnrichEmail(e.target.value)}
                    placeholder="e.g., patrick@stripe.com"
                    data-testid="enrich-email-input"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={handleEnrichPerson}
                    disabled={enriching || !enrichEmail.trim()}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid="enrich-person-btn"
                  >
                    {enriching ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Enriching...
                      </>
                    ) : (
                      <>
                        <User className="w-4 h-4 mr-2" />
                        Get Details
                      </>
                    )}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-3">
                Get detailed information about a person from their email address.
              </p>
              
              {/* Enrich Result */}
              {enrichResult && (
                <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                  {enrichResult.status === "success" ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="font-semibold text-green-700">Person Found!</span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {enrichResult.full_name && (
                          <div>
                            <span className="text-sm text-slate-500">Name:</span>
                            <p className="font-medium">{enrichResult.full_name}</p>
                          </div>
                        )}
                        {enrichResult.position && (
                          <div>
                            <span className="text-sm text-slate-500">Position:</span>
                            <p className="font-medium">{enrichResult.position}</p>
                          </div>
                        )}
                        {enrichResult.company && (
                          <div>
                            <span className="text-sm text-slate-500">Company:</span>
                            <p className="font-medium">{enrichResult.company}</p>
                          </div>
                        )}
                        {enrichResult.department && (
                          <div>
                            <span className="text-sm text-slate-500">Department:</span>
                            <p className="font-medium">{enrichResult.department}</p>
                          </div>
                        )}
                        {enrichResult.seniority && (
                          <div>
                            <span className="text-sm text-slate-500">Seniority:</span>
                            <p className="font-medium capitalize">{enrichResult.seniority}</p>
                          </div>
                        )}
                        {enrichResult.country && (
                          <div>
                            <span className="text-sm text-slate-500">Location:</span>
                            <p className="font-medium">{enrichResult.city ? `${enrichResult.city}, ` : ''}{enrichResult.country}</p>
                          </div>
                        )}
                        {enrichResult.linkedin && (
                          <div>
                            <span className="text-sm text-slate-500">LinkedIn:</span>
                            <a href={enrichResult.linkedin} target="_blank" rel="noopener noreferrer" 
                               className="text-blue-600 hover:underline text-sm block">View Profile</a>
                          </div>
                        )}
                        {enrichResult.twitter && (
                          <div>
                            <span className="text-sm text-slate-500">Twitter:</span>
                            <a href={`https://twitter.com/${enrichResult.twitter}`} target="_blank" rel="noopener noreferrer" 
                               className="text-blue-600 hover:underline text-sm block">@{enrichResult.twitter}</a>
                          </div>
                        )}
                        {enrichResult.phone && (
                          <div>
                            <span className="text-sm text-slate-500">Phone:</span>
                            <p className="font-medium">{enrichResult.phone}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-amber-600">
                      <User className="w-5 h-5" />
                      <span>No information found for {enrichResult.email}</span>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bulk Upload */}
        <TabsContent value="bulk">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Bulk Company Research
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleBulkUpload}
                  ref={fileInputRef}
                  className="hidden"
                  id="bulk-upload"
                  data-testid="bulk-upload-input"
                />
                <Upload className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                <p className="text-slate-600 mb-2">
                  Upload an Excel or CSV file with company names
                </p>
                <p className="text-xs text-slate-500 mb-4">
                  Required: <strong>Company Name</strong> (or Company, Client)<br/>
                  Optional: <strong>Country</strong> (defaults to Nigeria), <strong>Domain</strong> (e.g., company.com)
                </p>
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="bulk-upload-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Select File
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Discovery Search */}
        <TabsContent value="discover">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Globe className="w-5 h-5" />
                Discover New Leads by Industry
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label>Industry</Label>
                  <Select value={industry} onValueChange={setIndustry}>
                    <SelectTrigger className="mt-1" data-testid="research-industry-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {INDUSTRIES.map((ind) => (
                        <SelectItem key={ind} value={ind}>
                          {ind}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Location</Label>
                  <Select value={location} onValueChange={setLocation}>
                    <SelectTrigger className="mt-1" data-testid="research-location-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((loc) => (
                        <SelectItem key={loc} value={loc}>
                          {loc}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Number of Results</Label>
                  <Select value={limit} onValueChange={setLimit}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5 companies</SelectItem>
                      <SelectItem value="10">10 companies</SelectItem>
                      <SelectItem value="20">20 companies</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={handleDiscoverySearch}
                    disabled={searching}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid="research-search-btn"
                  >
                    {searching ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Searching...
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        Find Companies
                      </>
                    )}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-3">
                AI will suggest companies and Hunter.io will find contact details.
                This may take 30-60 seconds.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Results */}
      <div>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="text-lg font-semibold text-slate-900" data-testid="research-results-heading">
            Research Leads — {collectionTotal} held
          </h2>
          <div className="flex items-center gap-1 flex-wrap" data-testid="lead-view-tabs">
            {[
              { k: "working", label: "Working" },
              { k: "new", label: "New", c: statusCounts.new },
              { k: "parked", label: "Parked", c: statusCounts.parked },
              { k: "no_results", label: "No results", c: statusCounts.no_results },
              { k: "converted", label: "Converted", c: statusCounts.converted },
              { k: "rejected", label: "Not worth it", c: statusCounts.rejected },
              { k: "all", label: "All", c: collectionTotal },
            ].map((t) => (
              <button
                key={t.k}
                onClick={() => setLeadView(t.k)}
                data-testid={`lead-view-${t.k}`}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                  leadView === t.k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                }`}
              >
                {t.label}{t.c != null ? ` (${t.c})` : ""}
              </button>
            ))}
          </div>
        </div>

        {/* Bulk action bar (PART 5) */}
        {selectedLeadIds.size > 0 && (
          <div className="flex items-center justify-between bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2 mb-3" data-testid="lead-bulk-bar">
            <span className="text-sm text-indigo-800" data-testid="lead-bulk-count">{selectedLeadIds.size} selected</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="text-amber-700 border-amber-300 hover:bg-amber-50" onClick={() => openDecision("park", { bulk: true })} data-testid="bulk-park-btn">Park until…</Button>
              <Button size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={() => openDecision("reject", { bulk: true })} data-testid="bulk-reject-btn">Not worth it</Button>
              <Button size="sm" variant="ghost" onClick={() => setSelectedLeadIds(new Set())} data-testid="bulk-clear-btn">Clear</Button>
            </div>
          </div>
        )}

        {leads.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Globe className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500" data-testid="leads-empty">
                No leads in this view.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {leads.map((lead, idx) => (
              <Card
                key={`${lead.lead_id}-${idx}`}
                className={`${
                  lead.status === "converted"
                    ? "bg-emerald-50 border-emerald-200"
                    : lead.status === "no_results"
                    ? "bg-amber-50 border-amber-200"
                    : ""
                }`}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {["new", "no_results", "parked"].includes(lead.status || "new") && (
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 cursor-pointer accent-indigo-600"
                          checked={selectedLeadIds.has(lead.lead_id)}
                          onChange={() => toggleSelect(lead.lead_id)}
                          data-testid={`lead-select-${lead.lead_id}`}
                        />
                      )}
                      <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                        <Building className="w-5 h-5 text-slate-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">
                          {lead.company_name}
                        </h3>
                        <p className="text-sm text-slate-500">{lead.domain || "No domain found"}</p>
                      </div>
                    </div>
                    {lead.status === "converted" ? (
                      <Badge className="bg-emerald-100 text-emerald-700">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Converted
                      </Badge>
                    ) : lead.status === "rejected" ? (
                      <Badge variant="outline" className="text-red-600 border-red-300">Not worth it</Badge>
                    ) : lead.status === "parked" ? (
                      <Badge variant="outline" className="text-amber-700 border-amber-400">Parked → {lead.park_until}</Badge>
                    ) : lead.status === "no_results" ? (
                      <Badge variant="outline" className="text-amber-600 border-amber-300">
                        No Contacts
                      </Badge>
                    ) : (
                      <Badge variant="secondary">New</Badge>
                    )}
                  </div>

                  {/* G3a — decision trail: what it became / why / who / when */}
                  {lead.status === "converted" && lead.converted_client_id && (
                    <p className="text-xs text-emerald-700 mb-2" data-testid={`lead-converted-link-${lead.lead_id}`}>
                      Became client:{" "}
                      <button className="underline font-medium" onClick={() => navigate(`/clients/${lead.converted_client_id}`)}>
                        {lead.converted_client_name || "view client"}
                      </button>
                    </p>
                  )}
                  {lead.decision_reason && ["rejected", "parked", "no_results"].includes(lead.status) && (
                    <p className="text-xs text-slate-500 mb-2" data-testid={`lead-reason-${lead.lead_id}`}>
                      {lead.decision_reason}{lead.decided_by ? ` · ${lead.decided_by}` : ""}{lead.decided_at ? ` · ${String(lead.decided_at).slice(0, 10)}` : ""}
                    </p>
                  )}

                  <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {lead.location}
                    </span>
                    {lead.industry && lead.industry !== "Unknown" && (
                      <span>{lead.industry}</span>
                    )}
                    {lead.source && (
                      <Badge variant="outline" className="text-xs">
                        {lead.source === "company_search" ? "Search" : 
                         lead.source === "bulk_upload" ? "Bulk" : "Discovery"}
                      </Badge>
                    )}
                  </div>

                  {/* Contacts */}
                  {lead.contacts?.length > 0 && (
                    <div className="border-t pt-3 mt-3">
                      <p className="text-xs font-medium text-slate-500 mb-2">
                        <Users className="w-3 h-3 inline mr-1" />
                        Contacts Found ({lead.contacts.length})
                      </p>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {lead.contacts.map((contact, idx) => (
                          <div
                            key={idx}
                            className="text-sm bg-slate-50 rounded p-2"
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="font-medium text-slate-700">
                                  {contact.name || "Unknown"}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {contact.title}
                                </p>
                              </div>
                              {contact.confidence > 0 && (
                                <Badge variant="outline" className="text-xs">
                                  {contact.confidence}%
                                </Badge>
                              )}
                            </div>
                            <div className="mt-1 space-y-1">
                              {contact.email && (
                                <p className="flex items-center gap-1 text-xs text-blue-600">
                                  <Mail className="w-3 h-3" />
                                  {contact.email}
                                </p>
                              )}
                              {contact.phone && (
                                <p className="flex items-center gap-1 text-xs text-slate-600">
                                  <Phone className="w-3 h-3" />
                                  {contact.phone}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  {["new", "no_results", "parked"].includes(lead.status || "new") && (
                    <div className="flex gap-2 mt-4">
                      {(lead.status === "new" || !lead.status) && (
                        <>
                          <Button
                            size="sm"
                            className="flex-1 bg-blue-600 hover:bg-blue-700"
                            onClick={() => handleConvertToClient(lead.lead_id)}
                            data-testid={`convert-lead-${lead.lead_id}`}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Add to CRM
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                            onClick={() => handleOpenEnrichDialog(lead)}
                            data-testid={`enrich-lead-${lead.lead_id}`}
                          >
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Enrich Existing
                          </Button>
                        </>
                      )}
                      {lead.status === "no_results" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                          onClick={() => {
                            setCompanyName(lead.company_name || "");
                            setActiveTab("company");
                            toast.info("Re-search this company to try again");
                          }}
                          data-testid={`retry-lead-${lead.lead_id}`}
                        >
                          <RefreshCw className="w-4 h-4 mr-1" />
                          Re-search
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-amber-700 border-amber-300 hover:bg-amber-50"
                        onClick={() => openDecision("park", { leadId: lead.lead_id })}
                        data-testid={`park-lead-${lead.lead_id}`}
                      >
                        <Clock className="w-4 h-4 mr-1" /> Park
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => openDecision("reject", { leadId: lead.lead_id })}
                        data-testid={`reject-lead-${lead.lead_id}`}
                      >
                        Not worth it
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Enrich Existing Client Dialog */}
      <Dialog open={showEnrichDialog} onOpenChange={setShowEnrichDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Enrich Existing Client</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Add {selectedLeadForEnrich?.contacts?.length || 0} contacts from{" "}
              <strong>{selectedLeadForEnrich?.company_name}</strong> to an existing client.
            </p>
            <div>
              <Label>Select Client to Enrich</Label>
              <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                <SelectTrigger className="mt-1" data-testid="enrich-client-select">
                  <SelectValue placeholder="Choose a client..." />
                </SelectTrigger>
                <SelectContent>
                  {existingClients.map((client) => (
                    <SelectItem key={client.client_id} value={client.client_id}>
                      {client.company_name}
                      {client.contacts?.length > 0 && (
                        <span className="text-slate-400 ml-2">
                          ({client.contacts.length} contacts)
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-slate-500">
              This will add new contacts (without duplicates) and update missing sector/country info.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEnrichDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleEnrichExistingClient}
              disabled={!selectedClientId || enrichingClient}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-enrich-btn"
            >
              {enrichingClient ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enriching...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Enrich Client
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* G3a — decision modal: "not worth it" (reason required) / "park" (date required) */}
      <Dialog open={!!decisionModal} onOpenChange={(o) => { if (!o) setDecisionModal(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="lead-decision-dialog">
          <DialogHeader>
            <DialogTitle>
              {decisionModal?.kind === "park" ? "Park until a date" : "Not worth it"}
              {decisionModal?.bulk ? ` — ${decisionModal.count} leads` : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {decisionModal?.kind === "park" && (
              <div>
                <Label className="text-xs">Review date (required)</Label>
                <Input
                  type="date"
                  value={decisionDate}
                  onChange={(e) => setDecisionDate(e.target.value)}
                  data-testid="decision-date"
                />
              </div>
            )}
            <div>
              <Label className="text-xs">
                Reason{decisionModal?.kind === "reject" ? " (required)" : " (optional)"}
              </Label>
              <textarea
                value={decisionReason}
                onChange={(e) => setDecisionReason(e.target.value)}
                rows={3}
                className="w-full text-sm border border-slate-300 rounded p-2 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                placeholder={decisionModal?.kind === "park" ? "Why park it? (optional)" : "Why is this not worth pursuing?"}
                data-testid="decision-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDecisionModal(null)} data-testid="decision-cancel-btn">Cancel</Button>
            <Button
              onClick={submitDecision}
              disabled={savingDecision || (decisionModal?.kind === "reject" && !decisionReason.trim()) || (decisionModal?.kind === "park" && !decisionDate)}
              className={decisionModal?.kind === "park" ? "bg-amber-600 hover:bg-amber-700" : "bg-red-600 hover:bg-red-700"}
              data-testid="decision-confirm-btn"
            >
              {savingDecision ? <Loader2 className="w-4 h-4 animate-spin" /> : (decisionModal?.kind === "park" ? "Park" : "Mark not worth it")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Block1 — review & accept a discovery / bulk set. Nothing is created until accepted. */}
      <Dialog open={!!candidateSet} onOpenChange={(o) => { if (!o) declineCandidateSet(); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="candidate-set-dialog">
          <DialogHeader>
            <DialogTitle>
              Review {candidateSet?.source === "bulk_upload" ? "bulk upload" : "discovery"} results
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">
            Nothing here is on the lead board yet. Tick the ones worth keeping and accept — the rest are discarded.
          </p>
          <div className="space-y-2 mt-2" data-testid="candidate-list">
            {(candidateSet?.candidates || []).map((c) => {
              const f = c.found || {};
              const disabled = c.search_failed || c.contacts_found === 0 || !!c.existing_match;
              return (
                <div key={c.candidate_id} data-testid={`candidate-${c.candidate_id}`}
                     className={`flex items-start gap-3 p-3 rounded-lg border ${disabled ? "bg-slate-50 opacity-70" : "bg-white"}`}>
                  <input
                    type="checkbox"
                    className="mt-1"
                    data-testid={`candidate-check-${c.candidate_id}`}
                    disabled={disabled}
                    checked={!!candidateChecked[c.candidate_id]}
                    onChange={() => toggleCandidate(c.candidate_id)}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-800">{f.company_name || "—"}</span>
                      <span className="text-xs text-slate-400">{f.domain || ""}</span>
                      <Badge variant="outline" className="text-[10px]">{c.contacts_found} contacts</Badge>
                    </div>
                    <div className="text-[11px] text-slate-500">Searched: {c.searched}</div>
                    {c.name_mismatch && (
                      <div className="text-[11px] text-amber-700 mt-1" data-testid={`candidate-mismatch-${c.candidate_id}`}>
                        ⚠ You searched "{c.searched}" but this result is "{f.company_name}". Check it's the same company.
                      </div>
                    )}
                    {c.search_failed && (
                      <div className="text-[11px] text-rose-700 mt-1" data-testid={`candidate-failed-${c.candidate_id}`}>
                        No contacts found — a failed lookup, not a lead.
                      </div>
                    )}
                    {c.existing_match && (
                      <div className="text-[11px] text-indigo-700 mt-1" data-testid={`candidate-existing-${c.candidate_id}`}>
                        Already on record as a {c.existing_match.type}: {c.existing_match.name}. Open it instead of creating a new lead.
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={declineCandidateSet} data-testid="candidate-decline-all-btn">
              Decline all
            </Button>
            <Button onClick={acceptCandidateSet} disabled={acceptingSet} data-testid="candidate-accept-btn">
              {acceptingSet ? "Accepting…" : "Accept selected"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
